/** SSE plumbing shared by every Livy surface. Anthropic-shaped events, forwarded verbatim by the proxy. */

/**
 * Drains whole `event:`/`data:` frames from a buffer and returns the unconsumed remainder.
 * Chunks split mid-frame, so the caller keeps feeding the leftover back in.
 */
export function drainSse(buffer, handle) {
  let rest = buffer;
  let i;
  while ((i = rest.indexOf('\n\n')) !== -1) {
    const frame = rest.slice(0, i);
    rest = rest.slice(i + 2);
    let event = 'message';
    const data = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();else if (line.startsWith('data:')) data.push(line.slice(5).trim());
    }
    if (data.length) handle(event, data.join('\n'));
  }
  return rest;
}
export class StreamError extends Error {}
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A report schedule, exactly as property-service's Reports/scheduled takes it. */

const SCHEDULE_KEYS = ['AllocationAudit', 'AccountsReceivable', 'RecoveryKpis'];
const RANGE_RULES = ['LastClosedCycle', 'YearToDate', 'LastMonths'];
function scheduleFrom(parsed) {
  const kind = parsed.kind,
    reportKey = parsed.reportKey,
    rangeRule = parsed.rangeRule,
    rangeMonths = parsed.rangeMonths,
    propertyIds = parsed.propertyIds;
  const months = rangeMonths === null || typeof rangeMonths === 'number' && rangeMonths >= 1 && rangeMonths <= 24;
  return kind === 'schedule' && SCHEDULE_KEYS.includes(reportKey) && RANGE_RULES.includes(rangeRule) && months && Array.isArray(propertyIds) && propertyIds.every(id => typeof id === 'string' && GUID.test(id)) ? {
    reportKey,
    rangeRule,
    rangeMonths,
    propertyIds
  } : undefined;
}

/** A report email to switch, as offer_report_email_change named it. Saved by the person, never by the proxy. */

function emailOfferFrom(parsed) {
  const kind = parsed.kind,
    email = parsed.email,
    on = parsed.on;
  return kind === 'email' && (email === 'charge_export' || email === 'recovery_report') && typeof on === 'boolean' ? {
    email,
    on
  } : undefined;
}
/** The proxy already validated these; checked again because the download is built from them. */
function exportFrom(parsed) {
  const fromMonth = parsed.fromMonth,
    toMonth = parsed.toMonth,
    propertyIds = parsed.propertyIds;
  return typeof fromMonth === 'string' && MONTH.test(fromMonth) && typeof toMonth === 'string' && MONTH.test(toMonth) && Array.isArray(propertyIds) && propertyIds.every(id => typeof id === 'string' && GUID.test(id)) ? {
    fromMonth,
    toMonth,
    propertyIds: propertyIds
  } : undefined;
}
/**
 * The request id out of the tool result, rather than out of the model's prose.
 *
 * The confirmation card is the only place a raised request can be confirmed, so a card that
 * appears only when the model happens to repeat an id verbatim is a card that sometimes does not
 * appear at all. The tool's own result always carries it.
 */
function requestIdFrom(block) {
  var _block$content;
  const text = (_block$content = block.content) === null || _block$content === void 0 || (_block$content = _block$content.find(c => c.type === 'text')) === null || _block$content === void 0 ? void 0 : _block$content.text;
  if (!text) return undefined;
  try {
    const _ref = JSON.parse(text),
      requestId = _ref.requestId;
    return typeof requestId === 'string' && /^req_[0-9a-f]{8,}$/.test(requestId) ? requestId : undefined;
  } catch (_unused) {
    return undefined;
  }
}

/** Maps raw Anthropic stream events to the few things the UI actually renders. */
export function toDelta(event, raw) {
  var _message, _parsed$error;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_unused2) {
    return null;
  }
  if (event === 'livy_offer') {
    const offer = scheduleFrom(parsed);
    if (offer) return {
      offer
    };
    const emailOffer = emailOfferFrom(parsed);
    return emailOffer ? {
      emailOffer
    } : null;
  }
  if (event === 'livy_export') {
    const exportArgs = exportFrom(parsed);
    return exportArgs ? {
      exportArgs
    } : null;
  }
  if (event === 'error') throw new StreamError(String((_message = (_parsed$error = parsed.error) === null || _parsed$error === void 0 ? void 0 : _parsed$error.message) !== null && _message !== void 0 ? _message : 'stream error'));
  const block = parsed.content_block;
  if ((block === null || block === void 0 ? void 0 : block.type) === 'mcp_tool_use' && block.name) return {
    toolName: block.name
  };
  if ((block === null || block === void 0 ? void 0 : block.type) === 'text') return {
    blockStart: true
  };
  if ((block === null || block === void 0 ? void 0 : block.type) === 'mcp_tool_result') {
    const requestId = requestIdFrom(block);
    if (requestId) return {
      requestId
    };
  }
  const delta = parsed.delta;
  if ((delta === null || delta === void 0 ? void 0 : delta.type) === 'text_delta' && delta.text) return {
    text: delta.text
  };
  if (parsed.type === 'message_stop') return {
    done: true
  };
  return null;
}